'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, CheckSquare, Loader2, Search, Square, Tags } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { BaseDashboard, baseDashboardClasses } from '../BaseDashboard'
import { useStickyStackTop } from '../stickyStack'
import { useListReturn, LAST_OPENED_ROW_CLASS } from './useListReturn'
import { useDetailPath } from './useDetailPath'
import { ValueCell } from './ValueCell'
import { ResizableTable, TableTd, TableCheckboxTd, TableCheckboxHeaderLabel, rowClickToggleOrOpen, CHECKBOX_COL_WIDTH, CHECKBOX_COL_TH_CLASS, type ColumnDef } from '../ResizableTable'
import { SparkCard } from '../charts'
import { useScreenshotToken } from './useScreenshotToken'
import { apiGet, apiPost, useCatalog } from './entriesApi'
import { getEntryIsScam, getEntrySourceType } from './CasosView'
import { CaseEntryDetailPage } from './CaseEntryDetailPage'
import { BulkClassifyDialog, type BulkClassifyForm, type BulkClassifyResult } from './BulkClassifyDialog'
import type { EntryRow } from './entryTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { parseDbTimestamp } from '@/lib/dates'


const REVISION_LABEL: Record<string, string> = {
  unseen: 'Pendente',
  accept: 'Aceite',
  'auto-accept': 'Auto',
  'accept-hidden': 'Oculto',
  reject: 'Rejeitado',
  defer1: 'Adiado',
}

// raw_entries.last_pipeline_step — the entry's current position in the
// processing pipeline (raw_entry_success, imageocr_success/failed,
// classify_started/success/failed, classify_retry_success/failed,
// parsed_payload_update_success/failed, parsed_payload_missing, …) — a
// plain suffix convention, not a fixed enum, so classified by suffix rather
// than a lookup table: _started means the step is still running, _success
// means it finished, _failed means it errored. Anything matching neither
// (e.g. parsed_payload_missing) is informational rather than pass/fail.
type PipelineStepTone = 'success' | 'error' | 'active' | 'neutral'

function pipelineStepTone(step: string): PipelineStepTone {
  if (step.endsWith('_failed')) return 'error'
  if (step.endsWith('_started')) return 'active'
  if (step.endsWith('_success')) return 'success'
  return 'neutral'
}

const PIPELINE_STEP_TONE_CLASSES: Record<PipelineStepTone, string> = {
  success: 'border-success/40 text-success',
  error: 'border-destructive/40 text-destructive',
  active: 'border-signal/30 text-signal',
  neutral: 'border-border text-muted-foreground',
}

function humanizePipelineStep(step: string): string {
  return step.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Day-bucketed counts for the SparkCard mini charts (same "date" + numeric
// series shape SIBS's own dashboard aggregate feeds SparkCard with) — built
// client-side from whatever's in entries (page 0 plus any pages loaded via
// "Load more") rather than a dedicated aggregate RPC.
//
// The horizon is derived from the entries actually loaded, not a hardcoded
// "last 7 days" — that used to be disconnected from what the table itself
// shows: a high-volume source's single page can span a few hours (most of
// a fixed 7-day window would be empty bars), a low-volume one's can span
// weeks (older rows in the page would silently fall outside a fixed
// window and never get counted at all). Capped at MAX_CHART_DAYS so a
// genuinely sparse page (rows months apart) doesn't stretch the sparkline
// into an unreadable number of buckets — in that case it anchors to the
// most recent MAX_CHART_DAYS-day slice, same reasoning as any "recent
// activity" chart favoring freshness over completeness.
const MAX_CHART_DAYS = 30

function dayKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function dayLabel(key: string): string {
  const [, m, d] = key.split('-')
  return `${Number(d)}/${Number(m)}`
}

// The shared x-axis both SparkCards chart against — computed once from the
// full entries page so the "Number of entries" and "Non-<platform>
// platform" mini charts stay directly comparable (same days on both),
// rather than each independently horizoning on whatever subset they
// happen to be counting.
// `window` (optional) pins the axis to the dashboard's selected date range
// (from..to, inclusive) instead of deriving it from the loaded rows. The
// unified dashboard passes it so the chart matches the range picker: without
// it, selecting "Last 90 days" left the chart on the entries-derived 30-day
// cap, so the chart looked stuck at 30 days (reported 2026-10-29). Its own
// cap (WINDOW_MAX_CHART_DAYS) is higher so a 90-day pick renders in full,
// while a very wide pick still anchors to the most recent slice. The
// rows-derived path (Cases) is unchanged.
const WINDOW_MAX_CHART_DAYS = 92
export function buildDayRange(rows: EntryRow[], window?: { from: string; to: string }): string[] {
  const dayMs = 86400000

  let minDay: Date
  let maxDay: Date
  let cap: number
  if (window?.from && window?.to) {
    minDay = new Date(window.from + 'T00:00')
    maxDay = new Date(window.to + 'T00:00')
    if (!Number.isFinite(minDay.getTime()) || !Number.isFinite(maxDay.getTime())) return []
    cap = WINDOW_MAX_CHART_DAYS
  } else {
    const timestamps = rows
      .map((item) => (item.raw_entries ?? item)?.created_at)
      .filter((v): v is string => typeof v === 'string')
      .map((iso) => new Date(iso).getTime())
      .filter((t) => Number.isFinite(t))
    if (timestamps.length === 0) return []
    minDay = new Date(Math.min(...timestamps))
    maxDay = new Date(Math.max(...timestamps))
    cap = MAX_CHART_DAYS
  }
  minDay.setHours(0, 0, 0, 0)
  maxDay.setHours(0, 0, 0, 0)

  const trueSpanDays = Math.round((maxDay.getTime() - minDay.getTime()) / dayMs)
  const spanDays = Math.min(cap - 1, trueSpanDays)
  const start = new Date(maxDay.getTime() - spanDays * dayMs)

  const keys: string[] = []
  for (let i = 0; i <= spanDays; i++) keys.push(dayKey(new Date(start.getTime() + i * dayMs).toISOString()))
  return keys
}

export function bucketByDayRange(rows: EntryRow[], dayRange: string[], predicate?: (raw: any) => boolean) {
  const counts = new Map(dayRange.map((k) => [k, 0]))
  for (const item of rows) {
    const raw = item.raw_entries ?? item
    if (predicate && !predicate(raw)) continue
    if (!raw?.created_at) continue
    const key = dayKey(raw.created_at)
    if (counts.has(key)) counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return dayRange.map((key) => ({ date: dayLabel(key), count: counts.get(key) ?? 0 }))
}

// "Cases" — built on BaseDashboard, fetching from the exact same /api/entries
// (→ backend /allentries) queue "Conversation Cases" (CasosView, its sibling
// under Unified Entries) reads, via the same getEntryIsScam/getEntrySourceType
// derivation helpers (exported from CasosView.tsx rather than duplicated) so
// the two never drift on what counts as "scam" or "source". The source
// selector itself is the same single-select chip row CasosView's own
// FilterChips renders (not ResizableTable's column-filter dropdown — a
// different control), just placed inside the table's own card instead of a
// separate sidebar row, since this view has no sidebar to put it in.
//
// platformIds makes this a model for a per-tenant version of the same
// dashboard: /allentries now accepts an opt-in platform_ids filter (see its
// own comment on why raw_entries has to become an inner join for that),
// forwarded straight through by /api/entries's existing query-string
// passthrough — omit the prop and this behaves exactly like the unscoped
// "Cases" view does today.
//
// sourceType is the same idea but scoped by raw_entries_flat's own `source`
// column instead of platform — e.g. "ctt_trustfence_email" for CTT's
// inbound-email worker (routes/cloudflareWorkers/brands/ctt.ts). Deliberately
// NOT combined with
// platformIds: the whole point of a source-scoped view is to catch entries
// regardless of what platform they did or didn't get attributed to, so
// misattribution/classification bugs don't just silently vanish from every
// platform-scoped Cases view. The source chip row is skipped when this is
// set — every row already shares the one fixed source, so it'd only ever
// show a single, redundant chip.
//
// expectedPlatformCode pairs with sourceType — the platform (by
// platform.name_code, same lookup renderBrandCases/brandCasesPlatformIds in
// BackofficeApp.tsx already does) every entry from this source *should*
// carry. Entries whose raw_entries.platform_id doesn't match are exactly
// the misattribution failure mode sourceType exists to catch, so they get
// their own "Non-<platform> platform" mini chart alongside the entries
// count instead of only being findable by reading the whole table.
export function CasesDashboardView({
  theme,
  platformIds,
  sourceType,
  expectedPlatformCode,
  storageKey,
  isEditor,
  hiddenColumns,
  renderWidgets,
  eyebrow,
  title,
  subtitle,
  range,
  showIdColumns = false,
  timeRange,
  replayRows,
  headerActions,
  deepLink = false,
  hideRejected = false,
}: {
  theme: ProcessedTheme
  platformIds?: number[]
  sourceType?: string
  expectedPlatformCode?: string
  /** Namespaces the auto-refresh interval selection in localStorage (see
   * autoRefresh.ts) — this component is reused across every brand's Cases
   * view plus the unscoped Entries one, so callers must pass a value unique
   * to their own instance (e.g. the brand id) or interval choices would
   * bleed across brands. */
  storageKey: string
  /** Forwarded to CaseEntryDetailPage, which gates its Delete button on it
   * (backend also enforces Editor-only on /deleteuserentry). */
  isEditor?: boolean
  /** Column keys (matching `columns` below) to drop from both the header and
   * each row — e.g. per-tenant Cases views hide 'source'/'last_pipeline_step'
   * since those are moderation-oriented, cross-platform concerns that don't
   * apply once a brand already scopes the query to one platform. */
  hiddenColumns?: string[]
  /** Replaces the default SparkCard row (e.g. CTT Vendor Entries' own top
   * row). refreshKey bumps on every Refresh so the row can refetch too. */
  renderWidgets?: (ctx: { isDark: boolean; refreshKey: number; filtered: EntryRow[] }) => React.ReactNode
  /** Header overrides; the defaults follow sourceType as before. */
  eyebrow?: string
  title?: string
  subtitle?: string
  /** Bounds the table to created_at in [since, until) (ISO instants) — set
   * by a period picker (CTT Vendor Entries), refetches when it changes. */
  range?: { since: string; until: string }
  /** Domain / Beneficiary columns (raw_entries_flat's domain_url and
   * entity_reference_beneficiary; entity and reference stay on the entry's
   * detail). On for CTT Pilot Overview. */
  showIdColumns?: boolean
  /** Forwarded to BaseDashboard: the shared period picker in the header. */
  timeRange?: React.ComponentProps<typeof BaseDashboard>['timeRange']
  /** While non-null (a replay is running) the table shows these rows instead
   * of its own fetch, and refresh/load-more stand down; back to null refetches. */
  replayRows?: EntryRow[] | null
  /** Extra header controls (period picker, replay), left of Refresh. */
  headerActions?: React.ReactNode
  /** The open entry is the URL's last segment (/ctt/pilot-overview/{user_entry
   * id}), as on Unified Entries: linkable, reloadable, and a ctrl/middle-click
   * on a row opens it in a new tab. The board's slug must be allowed in
   * dashboardRoutes' hasDetailPage. On for CTT Pilot Overview. */
  deepLink?: boolean
  /** No entries reviewed as reject: the feed asks for hide_rejected, and an
   * entry rejected from its detail leaves the table too. A deep link to a
   * rejected entry still opens it. On for CTT Pilot Overview. */
  hideRejected?: boolean
}) {
  const { isDark, muted, border, panelBg } = baseDashboardClasses(theme)

  const [fetchedEntries, setEntries] = useState<EntryRow[]>([])
  const replayActive = replayRows != null
  const entries = replayRows ?? fetchedEntries
  // After the first load a range change refetches in place (refresh-style)
  // instead of swapping the whole page for the Loading… placeholder.
  const loadedOnceRef = useRef(false)
  const [total, setTotal] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [refreshKey, setRefreshKey] = useState(0)
  const [selected, setSelected] = useState<EntryRow | null>(null)
  const { catalog } = useCatalog()
  // Coming back from an entry: same scroll position, the row highlighted.
  const back = useListReturn()
  const detail = useDetailPath()
  // The dashboard's title bar is sticky above us; our own bar stacks under
  // it. The frame reports topbar + heading (onStackTop); read here, in the
  // view's own body, the context only holds the topbar.
  const outerTop = useStickyStackTop()
  const [stackTop, setStackTop] = useState(outerTop)

  // Measured so the table's own sticky column headers (ResizableTable's
  // stickyOffset) stack directly below this bar instead of underneath it —
  // it's caller content of variable height (the source-chip row is
  // conditional and can wrap), not something ResizableTable can size itself.
  // Callback ref, not a ref object read once on mount: the bar mounts after
  // the frame's loading state (and again when a detail page closes), so a
  // mount-time effect saw no element and the height stayed 0 -- the table's
  // column headers then pinned at the bar's top, behind it (staging,
  // 2026-10-22). Same pattern as DomainCandidatesDashboardView.
  const [stickyBarEl, setStickyBarEl] = useState<HTMLDivElement | null>(null)
  const [stickyBarHeight, setStickyBarHeight] = useState(0)
  useEffect(() => {
    if (!stickyBarEl) { setStickyBarHeight(0); return }
    const ro = new ResizeObserver(() => setStickyBarHeight(stickyBarEl.offsetHeight))
    ro.observe(stickyBarEl)
    setStickyBarHeight(stickyBarEl.offsetHeight)
    return () => ro.disconnect()
  }, [stickyBarEl])

  // Bulk classify — same dialog SibsMailView's raw-entries table uses, but
  // resolved server-side off user_entry_id (e.id here, always present)
  // instead of raw_entry_id. Every row is eligible to check — unlike
  // SibsMailView's canBulkClassify client-side guard, /bulkclassifyuserentries
  // resolves each entry's raw_entry_id authoritatively itself and reports a
  // clean per-entry failure for the rare row with none behind it (native
  // MBWay/call-derived), so there's nothing to pre-filter here.
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set())
  const [bulkClassifyOpen, setBulkClassifyOpen] = useState(false)
  const [bulkClassifySubmitting, setBulkClassifySubmitting] = useState(false)
  const [bulkClassifyResult, setBulkClassifyResult] = useState<BulkClassifyResult | null>(null)
  const toggleCheck = (id: string) =>
    setCheckedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  // Same platform.name_code lookup BackofficeApp.tsx's own
  // brandCasesPlatformIds/renderBrandCases uses to resolve a brand's
  // platform_id — reused here so "non-<platform> platform" reflects the
  // exact same platform identity Cases itself scopes by.
  const expectedPlatformId = useMemo(
    () => (expectedPlatformCode ? catalog.platforms.find((p) => p.name_code === expectedPlatformCode)?.id : undefined),
    [catalog.platforms, expectedPlatformCode],
  )
  const mismatchedEntries = useMemo(
    () => (expectedPlatformId == null ? [] : entries.filter((e) => (e.raw_entries ?? e).platform_id !== expectedPlatformId)),
    [entries, expectedPlatformId],
  )
  // Shared x-axis for both mini charts — see buildDayRange's own comment on
  // why this horizons on the entries actually loaded instead of a fixed
  // "last 7 days" window.
  const chartDayRange = useMemo(() => buildDayRange(entries), [entries])
  const entriesChartData = useMemo(() => bucketByDayRange(entries, chartDayRange), [entries, chartDayRange])
  const mismatchChartData = useMemo(
    () => bucketByDayRange(entries, chartDayRange, (raw) => expectedPlatformId != null && raw.platform_id !== expectedPlatformId),
    [entries, chartDayRange, expectedPlatformId],
  )

  // Screenshot token plumbing — same pattern SibsMailView/CttMailAlertView use
  // for their own row-click detail pages, needed by OriginalSubmissionWidget/
  // ScreenshotCaptures inside CaseEntryDetailPage.
  const { turnstile, getToken: getScreenshotToken } = useScreenshotToken()

  // Keyed off the joined string, not the array itself — a caller passing an
  // inline array literal (e.g. platformIds={[1, 2, 3]} on every render)
  // would otherwise give fetchEntries a new identity every render and
  // refetch in a loop.
  const platformIdsKey = platformIds && platformIds.length > 0 ? platformIds.join(',') : ''

  const fetchEntries = useCallback(async (targetPage = 0, isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else if (targetPage === 0) setLoading(true)
    else setLoadingMore(true)
    setError(null)
    const platformParam = platformIdsKey ? `&platform_ids=${platformIdsKey}` : ''
    const sourceParam = sourceType ? `&source=${encodeURIComponent(sourceType)}` : ''
    const rangeParam = range ? `&since=${encodeURIComponent(range.since)}&until=${encodeURIComponent(range.until)}` : ''
    // /casesentries (raw_entries_flat, see DATA_SOURCE_INFO) is the feed for
    // every Cases dashboard, platform-scoped, source-scoped, or unscoped —
    // the view exposes raw_entries.source directly, so a sourceType-scoped
    // instance (currently only CTT's raw-inbound-email dashboard) no longer
    // needs the legacy /allentries endpoint.
    const rejectedParam = hideRejected ? '&hide_rejected=1' : ''
    const result = await apiGet<{ entries: EntryRow[]; hasMore: boolean; total: number }>(`/cases?page=${targetPage}${platformParam}${sourceParam}${rangeParam}${rejectedParam}`)
    if (result.success && result.data) {
      setEntries((prev) => (targetPage === 0 ? result.data!.entries : [...prev, ...result.data!.entries]))
      // Server-side count against the exact same platform_ids/source filters
      // this fetch itself used (see getCasesEntriesFlat's own comment) — not
      // entries.length, which only reflects the pages loaded so far.
      setTotal(result.data.total)
      setHasMore(result.data.hasMore)
      setPage(targetPage)
      loadedOnceRef.current = true
    } else {
      setError(result.error ?? 'Failed to load data')
    }
    setLoading(false)
    setRefreshing(false)
    setLoadingMore(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformIdsKey, sourceType, range?.since, range?.until, hideRejected])

  useEffect(() => {
    if (replayActive) return
    fetchEntries(0, loadedOnceRef.current)
  }, [fetchEntries, replayActive])

  // deepLink: URL -> selected row, as UnifiedEntriesDashboardView does. Back/
  // Forward, the close button and a cold deep link all land here; a row on a
  // loaded page is used as is, anything else is fetched by id under the
  // list's platform/source scope (not its period: a link outlives the range).
  useEffect(() => {
    if (!deepLink) return
    const id = detail.id
    if (!id) { setSelected(null); return }
    if (selected?.id === id) return
    const onPage = entries.find((e) => e.id === id)
    if (onPage) { setSelected(onPage); return }
    let cancelled = false
    const platformParam = platformIdsKey ? `&platform_ids=${platformIdsKey}` : ''
    const sourceParam = sourceType ? `&source=${encodeURIComponent(sourceType)}` : ''
    apiGet<{ entries: EntryRow[] }>(`/cases?page=0&user_entry_id=${id}${platformParam}${sourceParam}`).then((result) => {
      if (cancelled) return
      const row = result.success ? result.data?.entries?.[0] : undefined
      if (row) setSelected(row)
      else detail.close()
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, detail.id])

  const sourceOptions = useMemo(
    () => Array.from(new Set(entries.map((e) => getEntrySourceType(e)).filter(Boolean))) as string[],
    [entries],
  )

  const filtered = useMemo(() => {
    let result = entries
    if (hideRejected) {
      result = result.filter((e) => e.revision_status !== 'reject')
    }
    if (sourceFilter !== 'all') {
      result = result.filter((e) => getEntrySourceType(e) === sourceFilter)
    }
    if (query) {
      const q = query.toLowerCase()
      result = result.filter((e) => {
        const raw = e.raw_entries ?? e
        return (
          String(raw.message_content ?? '').toLowerCase().includes(q) ||
          String(raw.sender ?? '').toLowerCase().includes(q)
        )
      })
    }
    return result
  }, [entries, sourceFilter, query, hideRejected])

  const toggleAll = () => {
    setCheckedIds((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map((e) => e.id)),
    )
  }

  const handleBulkClassify = async (form: BulkClassifyForm) => {
    const userEntryIds = filtered.filter((e) => checkedIds.has(e.id)).map((e) => e.id)
    if (userEntryIds.length === 0) return
    setBulkClassifySubmitting(true)
    setBulkClassifyResult(null)
    const result = await apiPost<{ results: Array<{ userEntryId: string; success: boolean; error?: unknown }> }>('/bulk-classify-user-entries', {
      userEntryIds,
      ...form,
    })
    setBulkClassifySubmitting(false)
    // results, not result.success — see SibsMailView's handleBulkClassify.
    if (result.data?.results) {
      const failures = result.data.results.filter((r) => !r.success)
      const succeededIds = new Set(result.data.results.filter((r) => r.success).map((r) => r.userEntryId))
      // Refetch page 0 rather than patching in place — the only way to pick
      // up whatever the RPC actually derived server-side (e.g. call-masked
      // sender). This does collapse any pages loaded beyond the first back
      // down to one, same tradeoff a manual refresh makes.
      if (succeededIds.size > 0) {
        setCheckedIds((prev) => {
          const next = new Set(prev)
          for (const id of succeededIds) next.delete(id)
          return next
        })
        await fetchEntries(0, true)
      }
      // BulkClassifyResult's failures are typed as { rawEntryId }, from
      // SibsMailView's original raw_entry_id-keyed usage — reused as-is
      // here holding userEntryId instead, since the dialog only ever reads
      // failures.length, never the id field itself.
      setBulkClassifyResult({ successCount: succeededIds.size, failures: failures.map((f) => ({ rawEntryId: f.userEntryId, error: f.error })) })
      if (failures.length === 0) setBulkClassifyOpen(false)
    } else {
      setBulkClassifyResult({
        successCount: 0,
        failures: userEntryIds.map((userEntryId) => ({ rawEntryId: userEntryId, error: result.error ?? 'Request failed' })),
      })
    }
  }

  const isColumnHidden = useCallback(
    (key: string) => hiddenColumns?.includes(key) ?? false,
    [hiddenColumns],
  )

  const columns: ColumnDef[] = useMemo(() => [
    ...(isEditor ? [{
      key: 'checkbox',
      initialWidth: CHECKBOX_COL_WIDTH,
      thClassName: CHECKBOX_COL_TH_CLASS,
      stickyLeft: true,
      label: (
        <TableCheckboxHeaderLabel
          checked={filtered.length > 0 && checkedIds.size === filtered.length}
          onToggle={toggleAll}
        />
      ),
    } as ColumnDef] : []),
    { key: 'last_pipeline_step', label: 'Pipeline', initialWidth: 150 },
    { key: 'source', label: 'Source', initialWidth: 140 },
    { key: 'platform', label: 'Platform', initialWidth: 120 },
    ...(showIdColumns ? [
      { key: 'domain', label: 'Domain', initialWidth: 180 },
      { key: 'beneficiary', label: 'Beneficiary', initialWidth: 180 },
    ] : []),
    { key: 'created_at', label: 'Created_At', initialWidth: 130 },
    { key: 'post_type', label: 'Post_Type', initialWidth: 90 },
    { key: 'sender', label: 'Sender', initialWidth: 160 },
    { key: 'message_content', label: 'Message_Content', initialWidth: 320 },
    { key: 'revision_status', label: 'Status', initialWidth: 110 },
  ].filter((c) => !isColumnHidden(c.key)), [isColumnHidden, isEditor, filtered, checkedIds, showIdColumns])

  const platformName = useCallback(
    (platformId: number | null | undefined) => catalog.platforms.find((p) => p.id === platformId)?.name ?? null,
    [catalog.platforms],
  )

  const fmtDate = (iso?: string) => {
    const d = parseDbTimestamp(iso)
    if (!d) return '—'
    const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
    const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
    return `${time} ${date}`
  }

  return (
    <>
      {turnstile}
      {isEditor && (
        <BulkClassifyDialog
          open={bulkClassifyOpen}
          onOpenChange={setBulkClassifyOpen}
          count={checkedIds.size}
          catalog={catalog}
          theme={theme}
          submitting={bulkClassifySubmitting}
          result={bulkClassifyResult}
          onSubmit={handleBulkClassify}
        />
      )}
      {selected ? (
        <CaseEntryDetailPage
          entry={selected}
          theme={theme}
          catalog={catalog}
          onClose={deepLink ? detail.close : () => setSelected(null)}
          getScreenshotToken={getScreenshotToken}
          isEditor={isEditor}
          tenantCode={storageKey}
          onDeleted={(userEntryId) => {
            setEntries((prev) => prev.filter((e) => e.id !== userEntryId))
            setTotal((prev) => (prev != null ? prev - 1 : prev))
          }}
          onUpdated={(userEntryId, patch) => {
            setEntries((prev) => prev.map((e) => (e.id === userEntryId ? { ...e, ...patch } : e)))
            setSelected((prev) => (prev && prev.id === userEntryId ? { ...prev, ...patch } : prev))
          }}
        />
      ) : (
    <BaseDashboard
      rootRef={back.restoreRef}
      theme={theme}
      eyebrow={eyebrow ?? (sourceType ? 'Raw · Source Inbound Email' : 'Moderation · Old Cases')}
      title={title ?? (sourceType ? 'Source Inbound Email' : 'Old Cases')}
      subtitle={
        subtitle ?? (sourceType
          ? `Every entry with source "${sourceType}", regardless of platform attribution — catches misattribution/classification bugs that would otherwise hide an entry from every platform-scoped Cases view.`
          : 'Same moderation queue as Conversation Cases, as a table.')
      }
      infoContent={
        <>
          <p><span className="font-semibold">raw_entries_flat</span> — one row per raw entry, with its latest domain, entity-reference, and full pipeline-step trace already joined in.</p>
          <p className="mt-1.5">Only entries with a linked user_entries record are shown (Cases is a moderation queue over those, not raw submissions).</p>
          {sourceType && (
            <p className="mt-1.5">Filtered server-side to <span className="font-mono">source = &quot;{sourceType}&quot;</span>, regardless of platform attribution.</p>
          )}
        </>
      }
      // Guarded, not just disabled: RefreshControl's auto-refresh timer calls
      // onRefresh directly and must not fetch mid-replay either (SIBS's rule).
      onRefresh={() => { if (replayActive) return; fetchEntries(0, true); setRefreshKey((k) => k + 1) }}
      refreshing={refreshing || replayActive}
      onStackTop={setStackTop}
      loading={loading}
      error={error}
      onRetry={() => { if (replayActive) return; fetchEntries(0, true) }}
      timeRange={timeRange}
      headerActions={headerActions}
      storageKey={`cases-${storageKey}-header`}
      widgets={renderWidgets ? renderWidgets({ isDark, refreshKey, filtered }) : (
        <>
          {/* Same SparkCard mini-chart SIBS's own Custom Mail Alert dashboard
              leads with (label + big number + trailing sparkline) — the
              sparkline is a day-bucketing of whatever's currently loaded
              (page 0 plus any pages pulled in via "Load more"), while the
              number itself is the exact server-side count for this same
              platform_ids/source-scoped query. */}
          <SparkCard
            id="cases-total"
            label="Number of entries"
            value={total ?? entries.length}
            chartData={entriesChartData}
            series={[{ key: 'count', color: 'var(--signal)' }]}
            isDark={isDark}
            className="h-28"
          />
          {expectedPlatformCode && (
            <SparkCard
              id="cases-non-platform"
              label={`Non-${expectedPlatformCode} platform`}
              value={mismatchedEntries.length}
              trend={expectedPlatformId == null ? 'platform unresolved' : undefined}
              chartData={mismatchChartData}
              series={[{ key: 'count', color: 'var(--destructive)' }]}
              isDark={isDark}
              className="h-28"
            />
          )}
        </>
      )}
      widgetCols={4}
      widgetRows="auto"
    >
      {/* Sticky as one block, right above the table's own sticky column
          headers (stickyOffset below tells ResizableTable how tall this is)
          — both scroll away together once the table itself scrolls past,
          same as any position:sticky content bounded by its own container. */}
      <div ref={setStickyBarEl} className="sticky z-40" style={{ top: stackTop }}>
        <div className={`px-4 py-3 border-b flex items-center gap-2 ${border} ${panelBg}`}>
          <Search className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
          <Input
            placeholder="Filter by message, sender…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-7 border-0 shadow-none bg-transparent focus-visible:ring-0 px-0 text-[13px]"
          />
          <span className={`font-mono text-[10px] uppercase tracking-widest whitespace-nowrap ${muted}`}>
            {filtered.length} / {entries.length}
          </span>
          {isEditor && filtered.length > 0 && (
            <button
              onClick={toggleAll}
              className={`ml-2 flex items-center gap-1.5 px-2.5 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors shrink-0 ${border} ${panelBg} ${muted} hover:border-signal hover:text-signal`}
            >
              {checkedIds.size === filtered.length ? <CheckSquare className="h-3 w-3" /> : <Square className="h-3 w-3" />}
              {checkedIds.size === filtered.length ? 'Deselect all' : 'Select all'}
            </button>
          )}
          {isEditor && checkedIds.size > 0 && (
            <button
              onClick={() => { setBulkClassifyResult(null); setBulkClassifyOpen(true) }}
              className={`ml-2 flex items-center gap-1.5 px-2.5 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors shrink-0 ${border} ${panelBg} ${muted} hover:border-signal hover:text-signal`}
            >
              <Tags className="h-3 w-3" />
              {`Classify ${checkedIds.size}`}
            </button>
          )}
        </div>
        {/* Source selection, built into the table card — the same single-select
            chip row CasosView's own FilterChips renders, just here instead of
            a separate sidebar. */}
        {!sourceType && sourceOptions.length > 0 && (
          <div className={`flex flex-wrap gap-1.5 px-4 py-2.5 border-b ${border} ${panelBg}`}>
            {[{ value: 'all', label: 'All sources' }, ...sourceOptions.map((s) => ({ value: s, label: s }))].map((o) => (
              <button
                key={o.value}
                onClick={() => setSourceFilter(o.value)}
                className={`shrink-0 rounded-[4px] border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                  sourceFilter === o.value
                    ? 'border-signal bg-signal-surface text-signal'
                    : `${border} ${muted} hover:border-signal hover:text-signal`
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <ResizableTable
        columns={columns}
        stickyHeader
        stickyOffset={stickyBarHeight}
        theme={theme}
        isEmpty={filtered.length === 0}
        emptyMessage="No entries found"
      >
        {filtered.map((e) => {
          const raw = e.raw_entries ?? e
          const source = getEntrySourceType(e)
          const isScam = getEntryIsScam(e)
          const status = e.revision_status ?? 'unseen'
          const platform = platformName(raw.platform_id)
          const platformMismatch = expectedPlatformId != null && raw.platform_id !== expectedPlatformId
          return (
            <tr
              key={e.id}
              data-last-opened={back.isLast(e.id) || undefined}
              onClick={(ev) => {
                if (deepLink && (ev.ctrlKey || ev.metaKey)) { window.open(detail.href(e.id), '_blank', 'noopener'); return }
                rowClickToggleOrOpen(
                  !!isEditor && checkedIds.size > 0,
                  () => toggleCheck(e.id),
                  () => back.open(e.id, ev.currentTarget, () => {
                    setSelected(e)
                    if (deepLink) detail.open(e.id)
                  }),
                )
              }}
              onAuxClick={(ev) => { if (deepLink && ev.button === 1) window.open(detail.href(e.id), '_blank', 'noopener') }}
              className={`cursor-pointer border-b border-l-2 transition-colors border-border ${
                checkedIds.has(e.id) ? 'bg-signal-surface' : back.isLast(e.id) ? LAST_OPENED_ROW_CLASS : ''
              } ${
                isScam
                  ? 'border-l-destructive bg-danger-surface hover:bg-danger-surface/70'
                  : 'border-l-transparent hover:bg-secondary'
              }`}
            >
              {isEditor && (
                <TableCheckboxTd checked={checkedIds.has(e.id)} onToggle={() => toggleCheck(e.id)} sticky />
              )}
              {!isColumnHidden('last_pipeline_step') && (
                <TableTd className="font-mono">
                  {raw.last_pipeline_step ? (
                    <span className={`inline-block rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-widest whitespace-nowrap ${PIPELINE_STEP_TONE_CLASSES[pipelineStepTone(raw.last_pipeline_step)]}`}>
                      {humanizePipelineStep(raw.last_pipeline_step)}
                    </span>
                  ) : (
                    <span className={'text-subtle'}>—</span>
                  )}
                </TableTd>
              )}
              {!isColumnHidden('source') && (
                <TableTd className={`font-mono truncate text-muted-foreground`}>{source ?? '—'}</TableTd>
              )}
              {!isColumnHidden('platform') && (
                <TableTd className={`font-mono truncate ${platformMismatch ? 'text-destructive' : 'text-muted-foreground'}`} title={platformMismatch ? `Expected ${expectedPlatformCode}` : undefined}>
                  {platform ?? '—'}
                </TableTd>
              )}
              {showIdColumns && (
                <>
                  <TableTd><ValueCell value={raw.domain_url} mono isDark={isDark} /></TableTd>
                  <TableTd><ValueCell value={raw.entity_reference_beneficiary} isDark={isDark} /></TableTd>
                </>
              )}
              {!isColumnHidden('created_at') && (
                <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtDate(raw.created_at)}</TableTd>
              )}
              {!isColumnHidden('post_type') && (
                <TableTd className={`font-mono text-muted-foreground`}>{raw.post_type ?? '—'}</TableTd>
              )}
              {!isColumnHidden('sender') && (
                <TableTd className={'text-muted-foreground'}>{raw.sender ?? '—'}</TableTd>
              )}
              {!isColumnHidden('message_content') && (
                <TableTd><span className={`line-clamp-1 text-muted-foreground`}>{raw.message_content ?? '—'}</span></TableTd>
              )}
              {!isColumnHidden('revision_status') && (
                <TableTd className={`font-mono text-[11px] text-muted-foreground`}>{REVISION_LABEL[status] ?? status}</TableTd>
              )}
            </tr>
          )
        })}
      </ResizableTable>
      {hasMore && !replayActive && (
        <div className="flex justify-center py-3">
          <button
            onClick={() => fetchEntries(page + 1)}
            disabled={loadingMore}
            className={`flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 ${border} ${panelBg} ${muted} hover:border-signal hover:text-signal`}
          >
            {loadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            Load more
          </button>
        </div>
      )}
    </BaseDashboard>
      )}
    </>
  )
}
